/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  dataPixels,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  DEFAULT_ASSET_PAGE_RATIO,
  DEFAULT_DPR,
} from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { getDefinitionForType } from '../elements';

const RESIZE_WIDTH_DIRECTION = [1, 0];

/**
 * @param {?number|undefined} value The value.
 * @return {boolean} Whether the value has been set.
 */
function isNum(value) {
  return typeof value === 'number';
}

function getInsertedElementSize(
  type,
  width,
  height,
  attrs,
  ratio = 1,
  resource = null
) {
  const { updateForResizeEvent, defaultAttributes } =
    getDefinitionForType(type);

  // When fetched assets have a width and height of 0, we get their dimensions from the thumbnails
  // This solves a bunch of problems with loading, and displaying grids properly, but it also means
  // that the resource is usually bigger than the thumbnail
  // So when the width is the same as the thumbnail, we want to use the default 80% width and not the resource width
  const isResourceSizeFromThumbnail =
    resource?.sizes?.web_stories_thumbnail?.width === resource?.width;

  if (!isNum(width)) {
    if (isNum(height)) {
      // Height is known: use aspect ratio.
      width = height * ratio;
    } else if (resource && resource.width > 0 && !isResourceSizeFromThumbnail) {
      // Resource is available: take resource's width with DPR, but limit
      // to fit on the page (100% max).
      width = Math.min(
        resource.width * DEFAULT_DPR,
        PAGE_WIDTH * DEFAULT_ASSET_PAGE_RATIO
      );
    } else {
      // Default to 100% of the page.
      width = PAGE_WIDTH * DEFAULT_ASSET_PAGE_RATIO;
    }
  }

  if (!isNum(height) && updateForResizeEvent) {
    // Try resize API with width-only direction.
    // @todo should this get dataToEditorX, dataToEditorY from the context?
    height = updateForResizeEvent({
      element: {
        ...defaultAttributes,
        ...attrs,
      },
      direction: RESIZE_WIDTH_DIRECTION,
      newWidth: width,
    }).height;
  }

  if (!isNum(height)) {
    // Fallback to simple ratio calculation.
    height = width / ratio;
  }

  // Ensure that the element fits on the page.
  if (width > PAGE_WIDTH || height > PAGE_HEIGHT) {
    const pageRatio = PAGE_WIDTH / PAGE_HEIGHT;
    const newRatio = width / height;
    if (newRatio <= pageRatio) {
      width = Math.min(width, PAGE_WIDTH);
      height = width / newRatio;
    } else {
      height = Math.min(height, PAGE_HEIGHT);
      width = height * newRatio;
    }
  }

  width = dataPixels(width);
  height = dataPixels(height);

  return { width, height };
}

export default getInsertedElementSize;
